import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTakeProfitErrors, selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

export const TakeProfitCommercialMargin = () => {
  const track = useTrackEvent();
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);

  const { value } = useAppSelector((state) => {
    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} not found in state.`);
      return {};
    }
    if (tile.type !== 'TakeProfitOrder') {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} is not a TakeProfitOrder.`);
      return {};
    }

    return { value: tile.properties.commercialMargin };
  }, deepEqual);

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const commercialMarginError = errors?.commercialMargin;

  const validateTakeProfit = useValidateTakeProfit();

  const handleCommercialMarginChange = useDebounce((newValue: number | undefined) => {
    track('Order action', 'Commercial margin changed');
    validateTakeProfit({
      commercialMargin: newValue,
    });
  });

  return (
    <OrderRow labelId="order.fields.commercialMargin.label">
      <div className="input-group input-group-sm flex-nowrap">
        <OmsFieldErrorWrapper errors={commercialMarginError}>
          <SgNumberInput
            valuePosition="start"
            selectAllOnFocus
            value={value}
            maxDecimals={9}
            data-e2e="commercial-margin"
            onChange={handleCommercialMarginChange}
            onFocus={() => setIsModifyingField(true)}
            onBlur={() => setIsModifyingField(false)}
            size="sm"
            state={isDefined(commercialMarginError) ? 'invalid' : undefined}
          />
          <span className="input-group-text">Bps</span>
        </OmsFieldErrorWrapper>
      </div>
    </OrderRow>
  );
};
